import {
  Avatar,
  Button,
  Card,
  CardHeader,
  Divider,
  makeStyles,
} from '@material-ui/core';
import { Skeleton } from '@material-ui/lab';
import { EmptyView } from 'components/EmptyView';
import {
  useLocalStorageSettings,
  TableSettings,
  getTableSettingsStorageKey,
} from 'hooks';
import { fetchRelationsForCompany } from 'lib/Api/Matches';
import { stringAvatar } from 'lib/Helper/Avatars';
import {
  PagedRelations,
  Relation,
  buildRelationTree,
  RelationNode,
} from 'lib/Model/Relation';
import * as React from 'react';
import { useLocation } from 'react-router-dom';

const useStyles = makeStyles((theme) => ({
  card: { marginBottom: theme.spacing(2) },

  treeScroll: {
    padding: theme.spacing(2),
    overflowX: 'auto',
    textAlign: 'center',
  },

  tree: {
    display: 'inline-block',
    margin: theme.spacing(2, 0, 4),
    padding: 0,
    listStyle: 'none',
    textAlign: 'center',
    whiteSpace: 'nowrap',
  },

  treeUl: {
    position: 'relative',
    padding: '24px 0 0 0',
    margin: 0,
    listStyle: 'none',
    textAlign: 'center',
  },

  treeUlInner: {
    '&::before': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: '50%',
      transform: 'translateX(-50%)',
      borderLeft: `1px solid ${theme.palette.divider}`,
      height: 24,
      width: 0,
    },
  },

  treeLi: {
    display: 'inline-block',
    position: 'relative',
    textAlign: 'center',
    verticalAlign: 'top',
    padding: '24px 10px 0 10px',

    '&::before, &::after': {
      content: '""',
      position: 'absolute',
      top: 0,
      width: '50%',
      height: 24,
      borderTop: `1px solid ${theme.palette.divider}`,
    },
    '&::before': {
      right: '50%',
      borderRight: `1px solid ${theme.palette.divider}`,
      borderTopLeftRadius: 2,
    },
    '&::after': {
      left: '50%',
      borderLeft: `1px solid ${theme.palette.divider}`,
      borderTopRightRadius: 2,
    },

    '&:only-child::before, &:only-child::after': { display: 'none' },
    '&:only-child': { paddingTop: 0 },

    '&:first-child::before': { border: 'none' },
    '&:last-child::after': { border: 'none' },
  },

  nodeBox: {
    minWidth: 240,
    maxWidth: 320,
    margin: '0 auto',
    padding: theme.spacing(1.5, 2),
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 12,
    backgroundColor: theme.palette.background.paper,
    boxShadow: '0 1px 0 rgba(0,0,0,0.03)',
  },

  nodeHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: theme.spacing(1.5),
  },

  nodeTitle: {
    fontWeight: 600,
    padding: 0,
    textTransform: 'uppercase',
    lineHeight: 1.3,
    textAlign: 'center',
    flex: 1,
    minWidth: 0,
    wordBreak: 'break-word',
    justifyContent: 'center',
  },

  avatarSmall: {
    width: 26,
    height: 26,
    fontSize: 14,
    flexShrink: 0,
  },

  skeletonWrap: { padding: theme.spacing(2) },
  skeletonRow: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    marginBottom: theme.spacing(1.5),
  },
}));

interface Props {
  companyId: string;
  doCompanyRefresh(): void;
}

export const RelatedSuggestions: React.FC<Props> = ({ companyId }) => {
  const classes = useStyles();

  const [loading, setLoading] = React.useState(false);
  const [data, setData] = React.useState<PagedRelations | undefined>();
  const [pageNumber] = React.useState(0);

  const { pathname, hash } = useLocation();
  const [tableSettings] = useLocalStorageSettings<TableSettings>(
    getTableSettingsStorageKey(pathname, hash),
    { rowsPerPage: 25 },
  );

  const fetchData = React.useCallback(() => {
    setLoading(true);
    fetchRelationsForCompany(
      companyId,
      pageNumber + 1,
      tableSettings.rowsPerPage,
    ).then((d) => {
      setLoading(false);
      setData(d);
    });
  }, [companyId, pageNumber, tableSettings.rowsPerPage]);

  React.useEffect(() => {
    fetchData();
  }, [fetchData]);

  const onOpenDetail = (d: Relation) => {
    window.open(`/clients/${d.company_id}`);
  };

  const renderSkeleton = () => (
    <div className={classes.skeletonWrap}>
      {[...Array(5)].map((_, i) => (
        <div key={i} className={classes.skeletonRow}>
          <Skeleton variant="circle" width={26} height={26} />
          <Skeleton variant="text" width={`${Math.random() * 30 + 30}%`} />
        </div>
      ))}
    </div>
  );

  const renderNode = (node: RelationNode) => (
    <li key={node.company_id} className={classes.treeLi}>
      <div className={classes.nodeBox}>
        <div className={classes.nodeHeader}>
          <Avatar
            variant="rounded"
            {...stringAvatar(node.company_title)}
            className={classes.avatarSmall}
          />
          <Button
            className={classes.nodeTitle}
            color="primary"
            onClick={() => onOpenDetail(node)}
          >
            {node.company_title}
          </Button>
        </div>
      </div>

      {node.children?.length > 0 && (
        <ul className={`${classes.treeUl} ${classes.treeUlInner}`}>
          {node.children.map((c) => renderNode(c))}
        </ul>
      )}
    </li>
  );

  const trees = data?.data ? buildRelationTree(data.data) : [];

  return (
    <Card className={classes.card}>
      <CardHeader
        title="Affiliated Companies"
        subheader="Top-down view of related companies (family tree from Zendesk Sell)."
      />
      <Divider />

      <div className={classes.treeScroll}>
        {loading ? (
          renderSkeleton()
        ) : !trees.length ? (
          <EmptyView>
            No affiliated companies found.
            <br />
            This company is not part of any company family tree in Zendesk Sell.
          </EmptyView>
        ) : (
          <ul className={classes.tree}>
            {trees.map((root) => (
              <ul key={root.company_id} className={classes.treeUl}>
                {renderNode(root)}
              </ul>
            ))}
          </ul>
        )}
      </div>
    </Card>
  );
};
